import React, {useEffect, useMemo, useState} from 'react';
import {connect, useSelector} from 'react-redux';
import {useMutation, useQueryClient} from 'react-query';
import {toast} from 'react-toastify';
import Modal from './Modal';
import Select from 'react-select';
import PrimaryButton from './PrimaryButton';
import {
  Option,
  MultiValueRemove,
  DropdownIndicator,
  customStyles,
} from '../components';
import {addNotificationEmails} from 'requests/queries/settings';
import * as Actions from 'store/actions';

function AddEmailAddressModal({open, setOpen, team = {}, getTeamMembers}) {
  const {userDetails} = useSelector(state => state.auth);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (open && userDetails?.company?.id) {
      getTeamMembers('status=active&notifications=false');
    }
  }, [open, userDetails?.company?.id, getTeamMembers]);

  const assignedOptions = useMemo(() => {
    const uniqueMembers = [];
    const seenValues = new Set();

    (team?.members?.team || []).forEach(member => {
      const firstName = member?.user?.first_name || '';
      const lastName = member?.user?.last_name || '';
      const email = member?.user?.email;
      const value = `${firstName}+${lastName}+${email}`.toLowerCase();
      if (!seenValues.has(value) && firstName && lastName && email) {
        seenValues.add(value);
        uniqueMembers.push({
          value,
          label: `${firstName} ${lastName}`,
          email,
        });
      }
    });

    return uniqueMembers;
  }, [team?.members]);

  const calculatedHeight = useMemo(() => {
    if (!isMenuOpen) return '112px';
    const optionHeight = 48;
    const baseHeight = 112;
    const optionsCount = assignedOptions.length;
    const totalOptionsHeight = optionsCount * optionHeight;
    const calculated = baseHeight + totalOptionsHeight;
    return `${Math.min(calculated, 400)}px`;
  }, [isMenuOpen, assignedOptions.length]);

  const mutation = useMutation(data => addNotificationEmails(data), {
    onSuccess: data => {
      toast.success(data.message || 'Emails added successfully');
      queryClient.invalidateQueries(['notificationEmails']);
      setSelectedMembers([]);
      setOpen(false);
    },
    onError: error => {
      toast.error(error.response?.data?.error || 'Failed to add emails');
    },
  });

  const handleSubmit = () => {
    if (selectedMembers.length === 0) {
      toast.error('Please select at least one team member');
      return;
    }
    const emails = selectedMembers.map(member => member.email);
    mutation.mutate({emails});
  };

  const handleModalClose = () => {
    setOpen(false);
    setIsMenuOpen(false);
    setSelectedMembers([]);
  };

  return (
    <Modal
      show={open}
      onClose={handleModalClose}
      extraModalClasses="max-w-[528px] overflow-y-auto h-fit max-h-[95%] sm:max-h-full rounded-b-none sm:rounded-b-lg absolute bottom-0 sm:relative lookup-modal"
      modalPosition="justify-center sm:justify-end"
      modalTitle="Add new email address"
    >
      <p className=" pt-6 px-6 text-sm text-body text-start">
        Ensure timely communication across teams by adding relevant team members
        to email notifications. This allows them to receive alerts when the
        balance is low and other important account-related updates.
      </p>
      <div
        className={`px-6 py-8 ${isMenuOpen ? 'overflow-y-auto' : ''}`}
        style={{height: calculatedHeight}}
      >
        <Select
          isMulti
          multiValueLabel
          placeholder="Select email"
          className="basic-multi-select apps-multi-select"
          classNamePrefix="react-select"
          components={{
            DropdownIndicator,
            MultiValueRemove,
            Option,
          }}
          styles={customStyles}
          options={assignedOptions}
          menuPlacement="bottom"
          hideSelectedOptions={false}
          closeMenuOnSelect={false}
          allowSelectAll
          onMenuOpen={() => setIsMenuOpen(true)}
          onMenuClose={() => setIsMenuOpen(false)}
          value={selectedMembers}
          onChange={setSelectedMembers}
        />
      </div>
      <div className="w-full px-6 py-[14px] border-[0.5px] flex gap-4 border-transparent border-t-[#EFEFEF]">
        <PrimaryButton
          buttonText={mutation.isLoading ? 'Adding...' : 'Add'}
          className="w-full !h-10"
          onClick={handleSubmit}
          disabled={mutation.isLoading}
          loading={mutation.isLoading}
        />
      </div>
    </Modal>
  );
}

export default connect(state => ({team: state.team}), {
  getTeamMembers: Actions.getTeamMembers,
})(AddEmailAddressModal);
